import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { useState } from "react";
import { useDebouncedCallback } from "use-debounce/lib";

import { getAddress } from "../../../api/genericData";
import { ModelState } from "../../../interfaces/modelState";
import { postcodeAddSpaceSanity } from "../../InputFields/sanitizer/inputSanitizers";
import SwitchInput from "../../InputFields/SwitchInput";
import TextInput from "../../InputFields/TextInput";
import { V1, V3, V4, V5, V10, V21, V22 } from "../../InputFields/validator/validators";
import styles from "./AddressFields.module.scss";

const DEBOUNCE_MILLISECONDS = 500;

interface GetAddressResponse {
  Postcode: string;
  HouseNumber: number;
  HouseNumberAddition?: string;
  Street: string;
  City: string;
}

interface Address {
  postcode: string;
  houseNumber: number;
  houseNumberAddition?: string;
  street: string;
  city: string;
  hasAdditionalAddressDetails: boolean;
  location?: string;
}

interface Props {
  data: Partial<Address>;
  fields: Record<string, Field<string>>;
  modelState: ModelState | null;
  setModelState: (updatedModelState: ModelState) => void;
  onUpdateAddress: (data: Partial<Address>) => void;
  partialFieldName: string;
}

export const AddressFields = ({
  fields,
  data,
  modelState,
  setModelState,
  partialFieldName,
  onUpdateAddress,
}: Props) => {
  const getPropertyName = (prefix: string, fieldName: string) => {
    return [prefix, partialFieldName, fieldName].filter(Boolean).join(".");
  };

  const [showStreetAndCity, setShowStreetAndCity] = useState(
    data.postcode !== undefined && data.houseNumber !== undefined
  );

  const handlePostcode = (postcode: string) => {
    onUpdateAddress({ postcode });
    doAddressLookup(postcode, data.houseNumber);
  };

  const handleHouseNumber = (houseNumber: number) => {
    onUpdateAddress({ houseNumber });
    doAddressLookup(data.postcode, houseNumber);
  };

  const handleHouseNumberAddition = (houseNumberAddition: string) => {
    onUpdateAddress({ houseNumberAddition });
  };

  const handleHasAdditionalAddressDetails = (hasAdditionalAddressDetails: boolean) => {
    if (hasAdditionalAddressDetails) {
      onUpdateAddress({ hasAdditionalAddressDetails });
    } else {
      onUpdateAddress({ hasAdditionalAddressDetails, location: undefined });
    }
  };

  const handleLocation = (location: string) => {
    onUpdateAddress({ location });
  };

  const handleStreet = (street: string) => {
    onUpdateAddress({ street });
  };

  const handleCity = (city: string) => {
    onUpdateAddress({ city });
  };

  const doAddressLookup = useDebouncedCallback((postcode?: string, houseNumber?: number | null) => {
    if (postcode && houseNumber) {
      (getAddress(postcode, houseNumber) as Promise<GetAddressResponse>)
        .then((response) => {
          if (!showStreetAndCity) {
            setShowStreetAndCity(true);
          }

          onUpdateAddress({ street: response.Street, city: response.City });
        })
        .catch(() => {
          if (!showStreetAndCity) {
            setShowStreetAndCity(true);
          }
        });
    }
  }, DEBOUNCE_MILLISECONDS);

  return (
    <>
      <div className={styles.addressFields__wrapper}>
        <TextInput
          fieldData={fields}
          name="Postcode"
          fieldName={`Postcode${partialFieldName}`}
          sanitize={[postcodeAddSpaceSanity]}
          validators={[V5, V10(7), V21]}
          setModelState={setModelState}
          inputValue={data.postcode}
          modelState={modelState}
          propertyName={getPropertyName("model", "Postcode")}
          onChange={handlePostcode}
          onBlur={handlePostcode}
          required
        />
        <TextInput
          fieldData={fields}
          name="HouseNumber"
          fieldName={`HouseNumber${partialFieldName}`}
          validators={[V5, V1]}
          setModelState={setModelState}
          inputValue={data.houseNumber}
          modelState={modelState}
          propertyName={getPropertyName("model", "HouseNumber")}
          onBlur={handleHouseNumber}
          onChange={handleHouseNumber}
          required
        />
        <TextInput
          fieldData={fields}
          name="HouseNumberAddition"
          fieldName={`HouseNumberAddition${partialFieldName}`}
          validators={[V4, V10(15)]}
          setModelState={setModelState}
          inputValue={data.houseNumberAddition}
          modelState={modelState}
          propertyName={getPropertyName("model", "HouseNumberAddition")}
          onBlur={handleHouseNumberAddition}
        />
      </div>
      {showStreetAndCity && (
        <>
          <TextInput
            key={`street_${data.postcode}_${data.houseNumber}_${data.street}`}
            fieldData={fields}
            name="Street"
            fieldName={`Street${partialFieldName}`}
            validators={[V5, V10(35)]}
            setModelState={setModelState}
            inputValue={data.street}
            modelState={modelState}
            propertyName={getPropertyName("model", "Street")}
            onBlur={handleStreet}
            required
          />
          <TextInput
            key={`city_${data.postcode}_${data.houseNumber}_${data.city}`}
            fieldData={fields}
            name="City"
            fieldName={`City${partialFieldName}`}
            validators={[V5, V10(50), V22]}
            setModelState={setModelState}
            inputValue={data.city}
            modelState={modelState}
            propertyName={getPropertyName("model", "City")}
            onBlur={handleCity}
            required
          />
        </>
      )}
      <SwitchInput
        fieldData={fields}
        value={data.hasAdditionalAddressDetails}
        name="HasAdditionalAddressDetails"
        fieldName={`HasAdditionalAddressDetails${partialFieldName}`}
        onChange={handleHasAdditionalAddressDetails}
      />
      {data.hasAdditionalAddressDetails && (
        <TextInput
          fieldData={fields}
          name="Location"
          fieldName={`Location${partialFieldName}`}
          validators={[V3, V10(15)]}
          setModelState={setModelState}
          inputValue={data.location}
          modelState={modelState}
          propertyName={getPropertyName("model", "Location")}
          onBlur={handleLocation}
          required
        />
      )}
    </>
  );
};
